import './ReportPage.scss';
import 'react-grid-layout/css/styles.css';

import {
  BatterReportChartConfigDto,
  BatterReportChartDataDto,
  BatterSessionReportChartConfigDto,
  BatterSessionReportChartDataDto,
  BatterSingleSessionReportChartConfigDto,
  BatterSingleSessionReportChartDataDto,
  ChartType,
  ExternalBatterReportChartConfigDto,
  ExternalBatterReportChartDataDto,
  ExternalPitcherReportChartConfigDto,
  ExternalPitcherReportChartDataDto,
  PitcherReportChartConfigDto,
  PitcherReportChartDataDto,
  PitcherSessionReportChartConfigDto,
  PitcherSessionReportChartDataDto,
  PitcherSingleSessionReportChartConfigDto,
  PitcherSingleSessionReportChartDataDto,
  PracticeBatterSessionReportChartConfigDto,
  PracticeBatterSessionReportChartDataDto,
  PracticeBatterSingleSessionReportChartConfigDto,
  PracticeBatterSingleSessionReportChartDataDto,
  PracticePitcherSessionReportChartConfigDto,
  PracticePitcherSessionReportChartDataDto,
  PracticePitcherSingleSessionReportChartConfigDto,
  PracticePitcherSingleSessionReportChartDataDto,
  UmpireReportChartConfigDto,
  UmpireReportChartDataDto,
  UmpireReportForAdminChartConfigDto,
  UmpireReportForAdminChartDataDto,
  UmpireReportForSessionChartConfigDto,
  UmpireReportForSessionChartDataDto,
} from 'types/CodegenReportTypes';
import { chartIdTochartSetup } from 'utilities/chartIdToComponent';
import { Divider, Paper, Typography } from '@trackman/web-shared-components';
import { Layout, Responsive, WidthProvider } from 'react-grid-layout';
import { ReactNode, useMemo, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ChartWrapper, { DRAG_HANDLER_CLASS } from 'components/Report/ChartWrapper/ChartWrapper';
import { useChartTitle } from 'hooks/helperHooks';
import { useReportViewModeStore } from 'stores/ReportViewModeStore';
import { useChartVisibility } from 'hooks/useChartVisibility';

const ResponsiveGridLayout = WidthProvider(Responsive);

type Props = {
  page: number;
  header: ReactNode;
  isEditMode: boolean;
  isLoadingData: boolean;
  setEditingChart: (chartId: ChartType | undefined) => void;
  editingChart: ChartType | undefined;
} & (
  | {
      // Player Report - Pitcher
      layout: PitcherReportChartConfigDto | undefined;
      tempLayout: PitcherReportChartConfigDto | undefined;
      pageChartData: PitcherReportChartDataDto | undefined;
      setTempLayout: (layout: PitcherReportChartConfigDto) => void;
    }
  | {
      // Player Report - Batter
      layout: BatterReportChartConfigDto | undefined;
      tempLayout: BatterReportChartConfigDto | undefined;
      pageChartData: BatterReportChartDataDto | undefined;
      setTempLayout: (layout: BatterReportChartConfigDto) => void;
    }
  | {
      // Umpire Report - Admin Overview
      layout: UmpireReportForAdminChartConfigDto | undefined;
      tempLayout: UmpireReportForAdminChartConfigDto | undefined;
      pageChartData: UmpireReportForAdminChartDataDto | undefined;
      setTempLayout: (layout: UmpireReportForAdminChartConfigDto) => void;
    }
  | {
      // Umpire Report - All Sessions
      layout: UmpireReportChartConfigDto | undefined;
      tempLayout: UmpireReportChartConfigDto | undefined;
      pageChartData: UmpireReportChartDataDto | undefined;
      setTempLayout: (layout: UmpireReportChartConfigDto) => void;
    }
  | {
      // Umpire Report - Single Session
      layout: UmpireReportForSessionChartConfigDto | undefined;
      tempLayout: UmpireReportForSessionChartConfigDto | undefined;
      pageChartData: UmpireReportForSessionChartDataDto | undefined;
      setTempLayout: (layout: UmpireReportForSessionChartConfigDto) => void;
    }
  | {
      // Session Review Report - Pitcher Single
      layout: PitcherSingleSessionReportChartConfigDto | undefined;
      tempLayout: PitcherSingleSessionReportChartConfigDto | undefined;
      pageChartData: PitcherSingleSessionReportChartDataDto | undefined;
      setTempLayout: (layout: PitcherSingleSessionReportChartConfigDto) => void;
    }
  | {
      // Session Review Report - Pitcher Aggregated
      layout: PitcherSessionReportChartConfigDto | undefined;
      tempLayout: PitcherSessionReportChartConfigDto | undefined;
      pageChartData: PitcherSessionReportChartDataDto | undefined;
      setTempLayout: (layout: PitcherSessionReportChartConfigDto) => void;
    }
  | {
      // Session Review Report - Batter Single
      layout: BatterSingleSessionReportChartConfigDto | undefined;
      tempLayout: BatterSingleSessionReportChartConfigDto | undefined;
      pageChartData: BatterSingleSessionReportChartDataDto | undefined;
      setTempLayout: (layout: BatterSingleSessionReportChartConfigDto) => void;
    }
  | {
      // Session Review Report - Batter Aggregated
      layout: BatterSessionReportChartConfigDto | undefined;
      tempLayout: BatterSessionReportChartConfigDto | undefined;
      pageChartData: BatterSessionReportChartDataDto | undefined;
      setTempLayout: (layout: BatterSessionReportChartConfigDto) => void;
    }
  | {
      // Practice Report - Batter Aggregated
      layout: PracticeBatterSessionReportChartConfigDto | undefined;
      tempLayout: PracticeBatterSessionReportChartConfigDto | undefined;
      pageChartData: PracticeBatterSessionReportChartDataDto | undefined;
      setTempLayout: (layout: PracticeBatterSessionReportChartConfigDto) => void;
    }
  | {
      // Practice Report - Batter Single
      layout: PracticeBatterSingleSessionReportChartConfigDto | undefined;
      tempLayout: PracticeBatterSingleSessionReportChartConfigDto | undefined;
      pageChartData: PracticeBatterSingleSessionReportChartDataDto | undefined;
      setTempLayout: (layout: PracticeBatterSingleSessionReportChartConfigDto) => void;
    }
  | {
      // Practice Report - Pitcher Aggregated
      layout: PracticePitcherSessionReportChartConfigDto | undefined;
      tempLayout: PracticePitcherSessionReportChartConfigDto | undefined;
      pageChartData: PracticePitcherSessionReportChartDataDto | undefined;
      setTempLayout: (layout: PracticePitcherSessionReportChartConfigDto) => void;
    }
  | {
      // Practice Report - Pitcher Single
      layout: PracticePitcherSingleSessionReportChartConfigDto | undefined;
      tempLayout: PracticePitcherSingleSessionReportChartConfigDto | undefined;
      pageChartData: PracticePitcherSingleSessionReportChartDataDto | undefined;
      setTempLayout: (layout: PracticePitcherSingleSessionReportChartConfigDto) => void;
    }
  | {
      // External Report - Batter
      layout: ExternalBatterReportChartConfigDto | undefined;
      tempLayout: ExternalBatterReportChartConfigDto | undefined;
      pageChartData: ExternalBatterReportChartDataDto | undefined;
      setTempLayout: (layout: ExternalBatterReportChartConfigDto) => void;
    }
  | {
      // External Report - Pitcher
      layout: ExternalPitcherReportChartConfigDto | undefined;
      tempLayout: ExternalPitcherReportChartConfigDto | undefined;
      pageChartData: ExternalPitcherReportChartDataDto | undefined;
      setTempLayout: (layout: ExternalPitcherReportChartConfigDto) => void;
    }
);

export const PAGE_CLASS_FOR_PRINTING = 'report-layout-grid-page';

const ReportPage = memo(
  ({ layout, tempLayout, page, header, isEditMode, pageChartData, isLoadingData, setTempLayout, setEditingChart, editingChart }: Props) => {
    const { t } = useTranslation();

    return (
      <>
        <div className='ReportPage-page-indicator'>
          <Divider className='divider' />
          <Typography variant='label' component='div' color='#A0A0A0'>
            {t('page.start', { page })}
          </Typography>
          <Divider className='divider' />
        </div>
        <div className={`${PAGE_CLASS_FOR_PRINTING} ${isEditMode ? '' : 'not-editing'}`}>
          {header}
          {layout && tempLayout && (
            <Grid
              layout={layout}
              tempLayout={tempLayout}
              isEditMode={isEditMode}
              pageChartData={pageChartData}
              isLoadingData={isLoadingData}
              setTempLayout={setTempLayout}
              setEditingChart={setEditingChart}
              editingChart={editingChart}
            />
          )}
        </div>
      </>
    );
  }
);

type GridProps = {
  layout: Exclude<Props['layout'], undefined>;
  tempLayout: Exclude<Props['tempLayout'], undefined>;
  setTempLayout: Props['setTempLayout'];
  isEditMode: boolean;
  isLoadingData: boolean;
  pageChartData: Props['pageChartData'];
  setEditingChart: Props['setEditingChart'];
  editingChart: Props['editingChart'];
};

const Grid = memo(
  ({ layout, tempLayout, setTempLayout, isEditMode, pageChartData, isLoadingData, setEditingChart, editingChart }: GridProps) => {
    const { getChartTitle, getChartSubTitle, getHideChartTitleInGrid } = useChartTitle();
    const { toggleChartVisibility } = useChartVisibility(tempLayout, setTempLayout);
    const { viewMode } = useReportViewModeStore();

    const handleLayoutChange = useCallback(
      (newLayoutTiles: Layout[]) => {
        if (!isEditMode) return;

        const newLayout = Object.fromEntries(
          Object.entries(tempLayout).map(([key, chart]) => {
            const tile = newLayoutTiles.find((t) => t.i === chart.chartId);
            return [
              key,
              {
                ...chart,
                chartDrawingInfo: {
                  ...chart.chartDrawingInfo,
                  position:
                    viewMode === 'full'
                      ? { x: tile?.x ?? chart.chartDrawingInfo.position.x, y: tile?.y ?? chart.chartDrawingInfo.position.y }
                      : chart.chartDrawingInfo.position,
                  width: viewMode === 'full' ? (tile?.w ?? chart.chartDrawingInfo.width) : chart.chartDrawingInfo.width,
                  height: viewMode === 'full' ? (tile?.h ?? chart.chartDrawingInfo.height) : chart.chartDrawingInfo.height,
                },
              },
            ];
          })
        );
        setTempLayout(newLayout as any);
      },
      [isEditMode, tempLayout, setTempLayout]
    );

    const charts = useMemo(() => {
      return Object.values(tempLayout)
        .filter((chart) => !chart.isHidden)
        .map((chart) => {
          const { chartId, chartDrawingInfo, ...chartConfig } = chart;
          const setup = chartIdTochartSetup[chartId];

          if (!setup) {
            console.error(`Chart ${chartId} not found`);
            return null;
          }

          const { Chart, dataKey, configKey, Sidebar } = setup;
          const isEditingThisChart = editingChart === chartId;
          const isEditingOtherChart = editingChart != null && !isEditingThisChart;

          return (
            <Paper
              key={chartId}
              data-grid={
                {
                  i: chartId,
                  x: chartDrawingInfo.position.x,
                  y: chartDrawingInfo.position.y,
                  w: chartDrawingInfo.width,
                  h: chartDrawingInfo.height,
                  isDraggable: isEditMode && !isEditingOtherChart && viewMode === 'full',
                  isResizable: isEditMode && !isEditingOtherChart && viewMode === 'full',
                  resizeHandles: ['se', 'sw'],
                } satisfies Layout
              }
              className={isEditingOtherChart ? 'editing-other-chart' : ''}
            >
              <ChartWrapper
                chartTitle={getChartTitle(chartId, chartConfig)}
                chartSubTitle={getChartSubTitle(chartId, chartConfig)}
                hideChartTitle={getHideChartTitleInGrid(chartId)}
                isEditMode={isEditMode}
                isEditingThisChart={isEditingThisChart}
                isEditingOtherChart={isEditingOtherChart}
                isThisChartEditable={Sidebar !== undefined}
                onEditClick={() => setEditingChart(isEditingThisChart ? undefined : chartId)}
                onHideClick={() => toggleChartVisibility(chartId)}
              >
                <Chart
                  chartData={pageChartData?.[dataKey]}
                  isLoading={isLoadingData}
                  chartConfig={chartConfig as never}
                  savedChartConfig={layout[configKey] as never}
                />
              </ChartWrapper>
            </Paper>
          );
        });
    }, [
      layout,
      tempLayout,
      isEditMode,
      pageChartData,
      isLoadingData,
      editingChart,
      getChartTitle,
      getChartSubTitle,
      getHideChartTitleInGrid,
      setEditingChart,
      toggleChartVisibility,
    ]);

    return (
      <ResponsiveGridLayout
        className='report-layout-grid'
        cols={{ lg: 6, md: 6, sm: 1, xs: 1, xxs: 1 }}
        rowHeight={120}
        margin={[16, 16]}
        draggableHandle={`.${DRAG_HANDLER_CLASS}`}
        preventCollision={false}
        compactType='vertical'
        useCSSTransforms={false}
        onLayoutChange={handleLayoutChange}
        style={{ transition: 'opacity 300ms ease' }}
      >
        {charts}
      </ResponsiveGridLayout>
    );
  }
);

export default ReportPage;
